import { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FormFieldCheckbox from '@anwb/poncho/components/form-field-checkbox';
import FormFieldInput from '@anwb/poncho/components/form-field-input';
import Grid from '@anwb/poncho/components/grid';
import Panel from '@anwb/poncho/components/panel';
import { Tooltip } from '@anwb/poncho/components/tooltip';
import Typography from '@anwb/poncho/components/typography';
import { useApplicationSize } from '@anwb/poncho/providers/providers-application';
import { formatPrice } from '@anwb/poncho/utilities/formatters';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { setPaymentInAdvanceAmount, setPaymentInAdvanceChoice } from 'store/actions';
import {
	chosenDurationSelector,
	paymentInAdvanceAmountSelector,
	paymentInAdvanceChoiceSelector,
	paymentInAdvanceValidSelector,
	totalContractPriceSelector,
	totalPricePerMonthSelector,
} from 'store/selectors';
import { getMaxAllowedPaymentInAdvance, getMonthlyPriceReduction } from 'utils';

import { customTooltipTaggingEvent } from './utils/datalayerEvent';

import { PaymentInAdvanceContainer, PaymentInAdvanceText } from './styles/paymentInAdvance.styled';

export const PaymentInAdvance = () => {
	const paymentInAdvanceAmount = useSelector(paymentInAdvanceAmountSelector);
	const paymentInAdvanceChoice = useSelector(paymentInAdvanceChoiceSelector);
	const isPaymentInAdvanceValid = useSelector(paymentInAdvanceValidSelector);
	const chosenDuration = useSelector(chosenDurationSelector);
	const totalPricePerMonth = useSelector(totalPricePerMonthSelector);
	const totalContractPrice = useSelector(totalContractPriceSelector);
	const priceReduction = getMonthlyPriceReduction(Number(chosenDuration), paymentInAdvanceAmount);

	const [maxAllowedPayment, setMaxAllowedPayment] = useState(0);
	const [paymentChoice, setPaymentChoice] = useState(paymentInAdvanceChoice);
	const [paymentAmount, setPaymentAmount] = useState<number | undefined>(paymentInAdvanceAmount);

	const dispatch = useDispatch();
	const applicationSize = useApplicationSize();
	const gaFunnelInfo = useGaFunnelInfo();

	/**
	 * This useEffect is essential for initiating validation of the PaymentInAdvance input field whenever
	 * there is a change in the totalContractPrice. This change is necessary as the totalContractPrice
	 * is utilized in computing the maximum permissible payment amount.
	 */
	useEffect(() => {
		const maxPaymentAmount = getMaxAllowedPaymentInAdvance(totalContractPrice);
		setMaxAllowedPayment(maxPaymentAmount);

		if (paymentAmount !== undefined) {
			// Since the FormFieldInput lacks the functionality to re-validate
			// we do it programmatically by re-setting the value, this triggers form validation
			const previousValue = paymentAmount;
			setPaymentAmount(undefined);
			setTimeout(() => {
				setPaymentAmount(previousValue);
			}, 10);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [totalContractPrice]);

	const handlePaymentAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;

		if (value) {
			setPaymentAmount(Number(value));
			dispatch(setPaymentInAdvanceAmount(Number(value)));
			return;
		}

		setPaymentAmount(undefined);
		dispatch(setPaymentInAdvanceAmount(undefined));
	};

	const handlePaymentChoiceChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.checked;

		setPaymentChoice(value);
		dispatch(setPaymentInAdvanceChoice(value));

		if (!value) {
			setPaymentAmount(undefined);
			dispatch(setPaymentInAdvanceAmount(undefined));
		}
	};

	const textVariant = applicationSize === 'small' ? 'price-text-sm' : 'price-text-md';

	return (
		<Panel hasOuterSpacing={false}>
			<Panel.Header title="Optioneel" />
			<Panel.Content>
				<Typography variant="component-subtitle" tagName="h3">
					Vooruitbetaling
					<Tooltip
						inline
						customTaggingEvent={(_?: string, isOpen?: boolean) => {
							customTooltipTaggingEvent('vooruitbetaling', isOpen, gaFunnelInfo);
						}}
					>
						Met een eenmalige vooruitbetaling verlaag je je maandbedrag, krijg je een lagere
						BKR-registratie en kom je wellicht eerder door de financiële toetsing. Vooruitbetalen is
						vrijwillig en kan tot een maximum van 50% van het totaal van alle maandtermijnen. Tip:
						gebruik eventueel de restwaarde van je huidige auto als vooruitbetaling.
					</Tooltip>
				</Typography>

				<Typography>Betaal een bedrag vooruit en verlaag je maandbedrag.</Typography>

				<FormFieldCheckbox
					checked={paymentChoice}
					onChange={handlePaymentChoiceChange}
					label="Ja, ik wil vooruitbetalen "
					reference="prepayment-choice-checkbox"
					name="prepayment-choice-checkbox"
				/>

				{paymentChoice && (
					<PaymentInAdvanceContainer>
						<Grid>
							<Grid.Item>
								<FormFieldInput
									dataTest="payment-in-advance-input"
									type="number"
									pattern="[0-9]+"
									textValidationMessages={{
										stepMismatch: 'Voer een geheel getal in',
									}}
									name="payment-in-advance"
									reference="payment-in-advance"
									required
									min={1000}
									max={maxAllowedPayment}
									label="Vul een bedrag in"
									value={paymentAmount?.toString()}
									placeholder="Bijv.: 1000"
									onBlur={handlePaymentAmountChange}
									textInfo={
										<Typography variant="support-text" dataTest="min-max-allowed-payment">
											Je kunt een bedrag van minimaal € 1.000 en maximaal{' '}
											{formatPrice(maxAllowedPayment, {
												decimals: 0,
											})}{' '}
											vooruitbetalen.
										</Typography>
									}
								/>
							</Grid.Item>
							{isPaymentInAdvanceValid && (
								<>
									<Grid.Item inline justify="space-between">
										<PaymentInAdvanceText
											variant={textVariant}
											dataTest="montly-reduction-label"
											isGreen
										>
											Maandelijkse verlaging
										</PaymentInAdvanceText>
										<PaymentInAdvanceText
											variant={textVariant}
											dataTest="montly-reduction-amount"
											isGreen
										>
											{formatPrice(-priceReduction)}
										</PaymentInAdvanceText>
									</Grid.Item>
									<Grid.Item inline justify="space-between">
										<PaymentInAdvanceText
											variant={textVariant}
											dataTest="total-per-month-after-reduction-label"
										>
											Totaal per maand
										</PaymentInAdvanceText>
										<PaymentInAdvanceText
											variant={textVariant}
											dataTest="total-per-month-after-reduction-amount"
										>
											{formatPrice(totalPricePerMonth - priceReduction)}
										</PaymentInAdvanceText>
									</Grid.Item>
								</>
							)}
						</Grid>
					</PaymentInAdvanceContainer>
				)}
			</Panel.Content>
		</Panel>
	);
};
